import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fonts, colors, radii, layout, gradients } from "./designTokens";
import useIsMobile from "./hooks/useIsMobile";
import { useTheme } from "./ThemeContext";
import useAuth from "./hooks/useAuth";
import { splitLangPrefix, buildLocalizedPath, LANG_OVERRIDE_KEY } from "./utils/langRouting";

const LANGUAGES = [
  { code: "en", country: "gb", label: "English" },
  { code: "de", country: "de", label: "Deutsch" },
  { code: "it", country: "it", label: "Italiano" },
  { code: "ja", country: "jp", label: "日本語" },
  { code: "es", country: "es", label: "Español" },
  { code: "pt", country: "br", label: "Português (BR)" },
  { code: "fr", country: "fr", label: "Français" },
  { code: "ru", country: "ru", label: "Русский" },
  { code: "pl", country: "pl", label: "Polski" },
  { code: "tr", country: "tr", label: "Türkçe" },
];

const COUNTRY_NAMES = Object.fromEntries(LANGUAGES.map(l => [l.country, l.label]));
const FlagImg = ({ country, size = 22 }) => (
  <img
    src={`/flags/${country}.png`}
    alt={COUNTRY_NAMES[country] || country.toUpperCase()}
    width={size}
    height={Math.round(size * 0.67)}
    style={{ borderRadius: 3, objectFit: "cover", display: "block", flexShrink: 0 }}
  />
);

// Reusable dropdown shell
const Dropdown = ({ children, minWidth = 160 }) => (
  <div style={{
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 14,
    boxShadow: "0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.05)",
    minWidth,
    zIndex: 200,
    overflow: "hidden",
    animation: "fadeIn 0.15s ease-out",
  }}>
    {children}
  </div>
);

// Reusable dropdown item (link or button)
const DropItem = ({ to, onClick, icon, label, badge, active, danger }) => {
  const base = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: danger ? colors.error : active ? colors.green : colors.gray800,
    background: active ? colors.successBg : "transparent",
    textDecoration: "none",
    fontFamily: fonts.body,
    transition: "background 0.12s",
    whiteSpace: "nowrap",
    border: "none",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
  };
  const onOver = (e) => { e.currentTarget.style.background = danger ? colors.errorBg : colors.gray100; };
  const onOut  = (e) => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; };
  const content = (
    <>
      {icon && <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{icon}</span>}
      <span style={{ flex: 1 }}>{label}</span>
      {badge && (
        <span style={{
          background: colors.error, color: colors.white, borderRadius: 99,
          minWidth: 18, height: 18, fontSize: 10, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 4px", lineHeight: 1,
        }}>{badge}</span>
      )}
    </>
  );
  if (to) return <Link to={to} style={base} onClick={onClick} onMouseOver={onOver} onMouseOut={onOut}>{content}</Link>;
  return <button type="button" style={base} onClick={onClick} onMouseOver={onOver} onMouseOut={onOut}>{content}</button>;
};

export default function Header() {
  const { isDark, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [menuOpen,          setMenuOpen]          = useState(false);
  const [langOpen,          setLangOpen]          = useState(false);
  const [userMenuOpen,      setUserMenuOpen]      = useState(false);
  const [gallerieMenuOpen,  setGallerieMenuOpen]  = useState(false);
  const [funMenuOpen,       setFunMenuOpen]       = useState(false);
  const [knowledgeMenuOpen, setKnowledgeMenuOpen] = useState(false);
  const [unreadCount,       setUnreadCount]       = useState(
    () => parseInt(localStorage.getItem("unreadNotifications") || "0", 10)
  );
  const [scrolled, setScrolled] = useState(false);

  const langRef          = useRef(null);
  const userMenuRef      = useRef(null);
  const gallerieMenuRef  = useRef(null);
  const funMenuRef       = useRef(null);
  const knowledgeMenuRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLangCode = i18n.language?.slice(0, 2) || "en";
  const currentLang = LANGUAGES.find(l => l.code === currentLangCode) || LANGUAGES[0];
  const { basePath } = splitLangPrefix(location.pathname);

  // Nav-Links unter dem aktuellen Sprachpräfix halten (z.B. /de/gallery statt
  // /gallery), damit ein Klick von einer deutschen Seite nicht auf Englisch
  // zurückspringt. "/#..."-Anker (Scroll-Ziele auf der Startseite) bleiben
  // unverändert, die werden per handleHashClick separat behandelt.
  const localize = (to) => (to.startsWith("/#") ? to : buildLocalizedPath(currentLangCode, to));

  const changeLanguage = (lng) => {
    // Bewusste Wahl über den Sprachschalter — merken, damit LangLayout auf der
    // unpräfixierten URL künftig nicht mehr die Browsersprache draufbügelt,
    // falls der Nutzer sich hier aktiv für etwas anderes entschieden hat.
    window.localStorage.setItem(LANG_OVERRIDE_KEY, lng);
    // URL ist ansonsten die alleinige Quelle der Wahrheit — die Sprachänderung
    // selbst übernimmt LangLayout reaktiv, sobald sich die URL ändert (App.js).
    navigate({ pathname: buildLocalizedPath(lng, basePath), search: location.search, hash: location.hash });
  };

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Unread badge sync
  useEffect(() => {
    const handler = () => setUnreadCount(0);
    window.addEventListener("notif-read", handler);
    return () => window.removeEventListener("notif-read", handler);
  }, []);

  // Close all dropdowns on outside click or Escape key
  useEffect(() => {
    if (!langOpen && !userMenuOpen && !gallerieMenuOpen && !funMenuOpen && !knowledgeMenuOpen && !menuOpen) return;
    const onMouse = (e) => {
      if (langRef.current          && !langRef.current.contains(e.target))          setLangOpen(false);
      if (userMenuRef.current      && !userMenuRef.current.contains(e.target))      setUserMenuOpen(false);
      if (gallerieMenuRef.current  && !gallerieMenuRef.current.contains(e.target))  setGallerieMenuOpen(false);
      if (funMenuRef.current       && !funMenuRef.current.contains(e.target))       setFunMenuOpen(false);
      if (knowledgeMenuRef.current && !knowledgeMenuRef.current.contains(e.target)) setKnowledgeMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLangOpen(false); setUserMenuOpen(false);
        setGallerieMenuOpen(false); setFunMenuOpen(false); setKnowledgeMenuOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [langOpen, userMenuOpen, gallerieMenuOpen, funMenuOpen, knowledgeMenuOpen, menuOpen]);

  // Scroll sperren, während das mobile Menü offen ist (echtes Drawer-Verhalten
  // statt scrollbarem Akkordeon-Inhalt dahinter). "overflow: hidden" allein auf
  // body reicht nicht — manche Browser scrollen stattdessen documentElement.
  useEffect(() => {
    if (!isMobile || !menuOpen) return;
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isMobile, menuOpen]);

  // Auth state — zentral über useAuth(), reagiert auf "aof-auth-changed"
  const { userId, username: authUsername, email: authEmail, isModerator, waveAccess, logout } = useAuth();
  const isLoggedIn    = !!userId;
  const username      = authUsername || "";
  const userEmail     = authEmail    || "";
  const battleIsLive  = localStorage.getItem("aof_battle_live") === "1";
  const isAdmin       = !!localStorage.getItem("adminToken");
  const hasWaveAccess = isAdmin || waveAccess;

  const closeAll = () => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setGallerieMenuOpen(false);
    setFunMenuOpen(false);
    setKnowledgeMenuOpen(false);
    setLangOpen(false);
  };

  const handleLogout = () => {
    logout();
    setUnreadCount(0);
    closeAll();
    navigate(localize("/"));
    window.location.reload();
  };

  const handleHomeClick = () => { closeAll(); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Scrollt zu einem Anker-Abschnitt der Startseite (z. B. #upload) — von
  // einer Unterseite aus erst zur Startseite navigieren, dann scrollen.
  const scrollToHomeAnchor = (id) => {
    closeAll();
    if (basePath === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(buildLocalizedPath(currentLangCode, "/"));
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    }
  };

  const isDetailPage = /^\/(fart|user)\//.test(basePath) ||
    ["/privacy", "/impressum", "/faq"].includes(basePath);
  const handleBack = () => { if (window.history.length > 1) navigate(-1); else navigate(localize("/")); };

  // ── Link definitions (to-Ziele werden unten per localize() sprachpräfixiert) ──
  const primaryLinks = [
    { to: "/",        labelKey: "nav.home"   },
    { to: "/#upload", labelKey: "nav.upload" },
    { to: "/battle",  label: `⚡ ${t("nav.battle")}`, live: true },
  ].map(l => ({ ...l, to: localize(l.to) }));

  const galerieLinks = [
    { to: "/gallery", labelKey: "nav.gallery" },
    { to: "/users",   labelKey: "nav.users"   },
  ].map(l => ({ ...l, to: localize(l.to) }));

  const funLinks = [
    { to: "/fart-runner",   label: `🕹️ ${t("nav.game")}` },
    { to: "/fart-invaders", label: `👾 ${t("nav.invaders")}` },
    { to: "/map",           label: `🌍 ${t("nav.map")}` },
    { to: "/player",      label: `💨 ${t("nav.player")}` },
    { to: "/bomb",        label: `💣 ${t("nav.bomb")}` },
    ...(hasWaveAccess ? [{ to: "/wave", label: `🌊 ${t("nav.wave")}` }] : []),
    ...(isAdmin ? [{ to: "/synthesizer", label: `🎛️ ${t("nav.synth")}` }] : []),
  ].map(l => ({ ...l, to: localize(l.to) }));

  const knowledgeLinks = [
    { to: "/encyclopedia", labelKey: "nav.encyclopedia" },
    { to: "/tools",        label: `🩺 ${t("nav.tools")}` },
    { to: "/faq",          labelKey: "nav.faq" },
  ].map(l => ({ ...l, to: localize(l.to) }));

  // Mobile hamburger — grouped by category.
  // Primäre Aktionen (Hochladen · Battle) stehen als eigene Zeile ganz oben,
  // analog zu den Primary-Links der Desktop-Navbar.
  const mobilePrimaryLinks = [
    { to: localize("/#upload"), labelKey: "nav.upload", icon: "⬆️" },
    { to: localize("/battle"),  label: `⚡ ${t("nav.battle")}`, live: true },
  ];
  const mobileFunLinks = [
    { to: "/fart-runner",   label: `🕹️ ${t("nav.game")}` },
    { to: "/fart-invaders", label: `👾 ${t("nav.invaders")}` },
    { to: "/map",         label: `🌍 ${t("nav.map")}` },
    { to: "/player",      label: `💨 ${t("nav.player")}` },
    { to: "/bomb",        label: `💣 ${t("nav.bomb")}` },
    ...(hasWaveAccess ? [{ to: "/wave", label: `🌊 ${t("nav.wave")}` }] : []),
  ].map(l => ({ ...l, to: localize(l.to) }));
  const mobileGalerieLinks = [
    { to: "/gallery", label: `💨 ${t("nav.gallery")}` },
    { to: "/users",   label: `👥 ${t("nav.users")}` },
  ].map(l => ({ ...l, to: localize(l.to) }));
  const mobileWissenLinks = [
    { to: "/encyclopedia", label: `📚 ${t("nav.encyclopedia")}` },
    { to: "/tools",        label: `🩺 ${t("nav.tools")}` },
    { to: "/faq",          label: `❓ ${t("nav.faq")}` },
  ].map(l => ({ ...l, to: localize(l.to) }));


  // ── Shared nav-link style ────────────────────────────────────────────────
  const nlStyle = (active) => ({
    color: colors.green,
    textDecoration: "none",
    fontWeight: 600,
    padding: "5px 10px",
    borderRadius: radii.pill,
    transition: "background 0.15s",
    background: active ? colors.successBg : "transparent",
    fontFamily: fonts.body,
    fontSize: 14.5,
    display: "flex",
    alignItems: "center",
    gap: 4,
    userSelect: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  // ── Primary flat links ───────────────────────────────────────────────────
  const renderPrimaryLinks = () =>
    primaryLinks.map(nav => {
      const active = nav.to.startsWith("/#")
        ? location.hash === nav.to.slice(1)
        : location.pathname === nav.to;
      const label = nav.label ?? t(nav.labelKey);
      const onOver = (e) => { e.currentTarget.style.background = colors.successBg; };
      const onOut  = (e) => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; };
      const inner = (
        <>
          {label}
          {nav.live && battleIsLive && (
            <span aria-hidden="true" style={{
              display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              background: "#e53e3e", flexShrink: 0,
              animation: "liveBlink 1.2s ease-in-out infinite",
            }} />
          )}
        </>
      );
      const handleHashClick = (e) => {
        e.preventDefault();
        closeAll();
        const id = nav.to.slice(2); // "/#upload" → "upload"
        if (basePath === "/") {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          navigate(buildLocalizedPath(currentLangCode, "/"));
          setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 350);
        }
      };
      return nav.to.startsWith("/#")
        ? <a key={nav.to} href={nav.to} style={nlStyle(active)} onClick={handleHashClick} onMouseOver={onOver} onMouseOut={onOut}>{inner}</a>
        : <Link key={nav.to} to={nav.to} style={nlStyle(active)} onClick={splitLangPrefix(nav.to).basePath === "/" ? handleHomeClick : closeAll} onMouseOver={onOver} onMouseOut={onOut}>{inner}</Link>;
    });

  // ── Galerie ▾ dropdown ───────────────────────────────────────────────────
  const renderGalerieMenu = () => {
    const active = galerieLinks.some(n => location.pathname === n.to);
    return (
      <div ref={gallerieMenuRef} style={{ position: "relative" }}>
        <button
          type="button"
          aria-label={t("nav.galleries")}
          aria-expanded={gallerieMenuOpen}
          aria-haspopup="true"
          onClick={() => { setGallerieMenuOpen(o => !o); setFunMenuOpen(false); setKnowledgeMenuOpen(false); setLangOpen(false); setUserMenuOpen(false); }}
          style={{ ...nlStyle(active), border: "none" }}
          onMouseOver={(e) => { e.currentTarget.style.background = colors.successBg; }}
          onMouseOut={(e)  => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; }}
          onFocus={(e)     => { e.currentTarget.style.outline = "2px solid #22c55e"; e.currentTarget.style.outlineOffset = "2px"; }}
          onBlur={(e)      => { e.currentTarget.style.outline = "none"; }}
        >
          {t("nav.galleries")}
          <span style={{ fontSize: 10, opacity: 0.55, fontWeight: 700 }}>▾</span>
        </button>
        {gallerieMenuOpen && (
          <Dropdown minWidth={160}>
            {galerieLinks.map(nav => (
              <DropItem
                key={nav.to}
                to={nav.to}
                label={nav.label ?? t(nav.labelKey)}
                active={location.pathname === nav.to}
                onClick={closeAll}
              />
            ))}
          </Dropdown>
        )}
      </div>
    );
  };

  // ── 🎮 Fun ▾ dropdown ────────────────────────────────────────────────────
  const renderFunMenu = () => {
    const active = funLinks.some(n => location.pathname === n.to);
    return (
      <div ref={funMenuRef} style={{ position: "relative" }}>
        <button
          type="button"
          aria-label={t("nav.fun")}
          aria-expanded={funMenuOpen}
          aria-haspopup="true"
          onClick={() => { setFunMenuOpen(o => !o); setGallerieMenuOpen(false); setKnowledgeMenuOpen(false); setLangOpen(false); setUserMenuOpen(false); }}
          style={{ ...nlStyle(active), border: "none" }}
          onMouseOver={(e) => { e.currentTarget.style.background = colors.successBg; }}
          onMouseOut={(e)  => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; }}
          onFocus={(e)     => { e.currentTarget.style.outline = "2px solid #22c55e"; e.currentTarget.style.outlineOffset = "2px"; }}
          onBlur={(e)      => { e.currentTarget.style.outline = "none"; }}
        >
          🎮 {t("nav.fun")}
          <span style={{ fontSize: 10, opacity: 0.55, fontWeight: 700 }}>▾</span>
        </button>
        {funMenuOpen && (
          <Dropdown minWidth={160}>
            {funLinks.map(nav => (
              <DropItem
                key={nav.to}
                to={nav.to}
                label={nav.label ?? t(nav.labelKey)}
                active={location.pathname === nav.to}
                onClick={closeAll}
              />
            ))}
          </Dropdown>
        )}
      </div>
    );
  };

  // ── 📚 Wissen ▾ dropdown ─────────────────────────────────────────────────
  const renderKnowledgeMenu = () => {
    const active = knowledgeLinks.some(n => location.pathname === n.to);
    return (
      <div ref={knowledgeMenuRef} style={{ position: "relative" }}>
        <button
          type="button"
          aria-label={t("nav.knowledge")}
          aria-expanded={knowledgeMenuOpen}
          aria-haspopup="true"
          onClick={() => { setKnowledgeMenuOpen(o => !o); setGallerieMenuOpen(false); setFunMenuOpen(false); setLangOpen(false); setUserMenuOpen(false); }}
          style={{ ...nlStyle(active), border: "none" }}
          onMouseOver={(e) => { e.currentTarget.style.background = colors.successBg; }}
          onMouseOut={(e)  => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; }}
          onFocus={(e)     => { e.currentTarget.style.outline = "2px solid #22c55e"; e.currentTarget.style.outlineOffset = "2px"; }}
          onBlur={(e)      => { e.currentTarget.style.outline = "none"; }}
        >
          📚 {t("nav.knowledge")}
          <span style={{ fontSize: 10, opacity: 0.55, fontWeight: 700 }}>▾</span>
        </button>
        {knowledgeMenuOpen && (
          <Dropdown minWidth={170}>
            {knowledgeLinks.map(nav => (
              <DropItem
                key={nav.to}
                to={nav.to}
                label={nav.label ?? t(nav.labelKey)}
                active={location.pathname === nav.to}
                onClick={closeAll}
              />
            ))}
          </Dropdown>
        )}
      </div>
    );
  };

  // ── Mobile nav helpers ──────────────────────────────────────────────────
  const renderMobileNavLink = (nav) => {
    const label = nav.label ?? t(nav.labelKey);
    const active = location.pathname === nav.to;
    return (
      <Link
        key={nav.to}
        to={nav.to}
        onClick={closeAll}
        style={{
          color: active ? colors.green : colors.greenNav,
          textDecoration: "none",
          fontWeight: active ? 700 : 600,
          fontSize: 14,
          lineHeight: 1.25,
          padding: "9px 12px",
          minHeight: 44,
          boxSizing: "border-box",
          borderRadius: 10,
          background: active ? colors.successBg : "rgba(0,0,0,0.03)",
          display: "flex", alignItems: "center", gap: 6,
          border: active ? `1px solid ${colors.borderGold}` : "1px solid transparent",
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{label}</span>
        {nav.live && battleIsLive && (
          <span aria-hidden="true" style={{
            display: "inline-block", width: 7, height: 7, borderRadius: "50%",
            background: "#e53e3e", flexShrink: 0,
            animation: "liveBlink 1.2s ease-in-out infinite",
          }} />
        )}
      </Link>
    );
  };

  // Primäre Aktions-Kachel (Hochladen / Battle) — optisch hervorgehoben,
  // #upload wird per Anker-Scroll auf die Startseite aufgelöst.
  const renderMobilePrimaryLink = (nav) => {
    const label = nav.label ?? t(nav.labelKey);
    const text = nav.icon ? `${nav.icon} ${label}` : label;
    const isHash = nav.to.includes("#");
    const active = !isHash && location.pathname === nav.to;
    const style = {
      color: colors.green,
      textDecoration: "none",
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 1.25,
      padding: "9px 12px",
      minHeight: 44,
      boxSizing: "border-box",
      borderRadius: 10,
      background: colors.successBg,
      border: `1px solid ${active ? colors.borderGold : colors.borderGreen}`,
      display: "flex", alignItems: "center", gap: 6,
    };
    const liveDot = nav.live && battleIsLive && (
      <span aria-hidden="true" style={{
        display: "inline-block", width: 7, height: 7, borderRadius: "50%",
        background: "#e53e3e", flexShrink: 0,
        animation: "liveBlink 1.2s ease-in-out infinite",
      }} />
    );
    const textEl = <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{text}</span>;
    if (isHash) {
      const id = nav.to.slice(nav.to.indexOf("#") + 1);
      return (
        <a key={nav.to} href={nav.to} style={style}
           onClick={(e) => { e.preventDefault(); scrollToHomeAnchor(id); }}>
          {textEl}{liveDot}
        </a>
      );
    }
    return (
      <Link key={nav.to} to={nav.to} onClick={closeAll} style={style}>
        {textEl}{liveDot}
      </Link>
    );
  };

  const renderMobileSectionLabel = (label) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "8px 0 4px" }}>
      <div style={{ flex: 1, height: 1, background: colors.borderGold }} />
      <span style={{
        fontSize: 10, fontWeight: 700, color: colors.gray400,
        textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap",
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: colors.borderGold }} />
    </div>
  );

  const renderMobileUserCard = () => {
    if (!isLoggedIn) return null;
    return (
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <Link
          to={localize("/profile")}
          onClick={closeAll}
          style={{
            flex: 1, minWidth: 0,
            display: "flex", alignItems: "center", gap: 10,
            background: colors.successBg,
            border: `1px solid ${colors.borderGreen}`,
            borderRadius: radii.md,
            padding: "9px 12px",
            textDecoration: "none",
          }}
        >
          <span style={{
            width: 34, height: 34, borderRadius: "50%",
            background: `linear-gradient(135deg, ${colors.btnGreen}, #28c76f)`,
            color: "#0a3317", fontWeight: 800, fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, lineHeight: 1, fontFamily: fonts.body,
            userSelect: "none",
          }}>
            {userInitial}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{
              display: "block", fontSize: 10, color: colors.gray400,
              fontFamily: fonts.body, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: 0.6,
            }}>
              {t("nav.signedInAs")}
            </span>
            <span style={{
              display: "block", fontSize: 14.5, fontWeight: 700, color: colors.textDark,
              fontFamily: fonts.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {username || userEmail}
            </span>
          </span>
          {unreadCount > 0 && (
            <span aria-hidden="true" style={{
              background: colors.error, color: colors.white, borderRadius: "50%",
              minWidth: 18, height: 18, fontSize: 10, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 4px", lineHeight: 1, flexShrink: 0,
            }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <span aria-hidden="true" style={{ fontSize: 16, color: colors.green, opacity: 0.6, flexShrink: 0 }}>›</span>
        </Link>
        <Link
          to={localize("/feed")}
          onClick={closeAll}
          aria-label={t("nav.feed")}
          title={t("nav.feed")}
          style={{
            flexShrink: 0, minWidth: 48,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: basePath === "/feed" ? colors.successBg : "rgba(0,0,0,0.03)",
            border: `1px solid ${basePath === "/feed" ? colors.borderGold : colors.borderGreen}`,
            borderRadius: radii.md,
            fontSize: 20,
            textDecoration: "none",
          }}
        >
          📰
        </Link>
      </div>
    );
  };

  // ── Language picker ──────────────────────────────────────────────────────
  const renderLangPicker = (mobile = false) => (
    <div ref={mobile ? undefined : langRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label={`Change language, current: ${currentLang.label}`}
        aria-expanded={langOpen}
        aria-haspopup="listbox"
        onClick={() => { setLangOpen(o => !o); if (!mobile) { setUserMenuOpen(false); setGallerieMenuOpen(false); setFunMenuOpen(false); setKnowledgeMenuOpen(false); } }}
        style={{
          background: "none",
          border: `1px solid ${colors.borderGreen}`,
          borderRadius: radii.pill,
          padding: mobile ? "5px 10px" : "5px 9px",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 4,
          fontFamily: fonts.body, lineHeight: 1,
          transition: "border-color 0.15s",
        }}
      >
        <FlagImg country={currentLang.country} size={mobile ? 24 : 20} />
        <span style={{ fontSize: mobile ? 11 : 10, opacity: 0.55, fontWeight: 700 }}>▾</span>
      </button>
      {langOpen && (
        <div
          ref={mobile ? langRef : undefined}
          style={{
            position: mobile ? "relative" : "absolute",
            top: mobile ? 4 : "calc(100% + 6px)",
            right: mobile ? undefined : 0,
            background: colors.white,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: radii.sm,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 140, zIndex: 200, overflow: "hidden",
          }}
        >
          {LANGUAGES.map(lang => {
            const active = lang.code === currentLangCode;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => { changeLanguage(lang.code); setLangOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "9px 14px", border: "none",
                  background: active ? colors.successBg : "transparent",
                  fontWeight: active ? 700 : 500, fontSize: 14,
                  fontFamily: fonts.body, cursor: "pointer",
                  textAlign: "left", color: colors.green,
                  transition: "background 0.12s",
                }}
                onMouseOver={(e) => { if (!active) e.currentTarget.style.background = colors.gray100; }}
                onMouseOut={(e)  => { e.currentTarget.style.background = active ? colors.successBg : "transparent"; }}
              >
                <FlagImg country={lang.country} size={20} />
                {lang.label}
                {active && <span style={{ marginLeft: "auto", fontSize: 12, color: colors.green }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── User menu dropdown ───────────────────────────────────────────────────
  const userInitial = username.charAt(0).toUpperCase() || "?";
  const renderUserMenu = () => (
    <div ref={userMenuRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label={`${t("nav.profile")}: ${username || userEmail}`}
        aria-expanded={userMenuOpen}
        aria-haspopup="true"
        onClick={() => { setUserMenuOpen(o => !o); setLangOpen(false); setGallerieMenuOpen(false); setFunMenuOpen(false); setKnowledgeMenuOpen(false); }}
        style={{
          position: "relative",
          display: "flex", alignItems: "center", gap: 7,
          background: userMenuOpen ? colors.successBg : colors.white,
          border: `1.5px solid ${colors.btnGreen}`,
          borderRadius: radii.pill,
          padding: "4px 11px 4px 5px",
          cursor: "pointer",
          fontFamily: fonts.body,
          transition: "background 0.15s, box-shadow 0.15s",
          boxShadow: "0 1px 4px #e7ffd7",
        }}
        onMouseOver={(e) => { if (!userMenuOpen) { e.currentTarget.style.background = colors.successBg; e.currentTarget.style.boxShadow = "0 2px 8px #d5ffd0"; }}}
        onMouseOut={(e)  => { if (!userMenuOpen) { e.currentTarget.style.background = colors.white;     e.currentTarget.style.boxShadow = "0 1px 4px #e7ffd7"; }}}
      >
        {/* Avatar circle */}
        <span style={{
          width: 26, height: 26, borderRadius: "50%",
          background: `linear-gradient(135deg, ${colors.btnGreen}, #28c76f)`,
          color: "#0a3317", fontWeight: 800, fontSize: 13,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, lineHeight: 1, fontFamily: fonts.body,
          userSelect: "none",
        }}>
          {userInitial}
        </span>
        <span style={{
          fontSize: 14, fontWeight: 600, color: colors.greenProfile,
          maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {username || t("nav.profile")}
        </span>
        <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 700 }}>▾</span>
        {/* Unread badge on trigger button */}
        {unreadCount > 0 && (
          <span aria-hidden="true" style={{
            position: "absolute", top: -5, right: -5,
            background: colors.error, color: colors.white, borderRadius: "50%",
            minWidth: 18, height: 18, fontSize: 10, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", boxShadow: "0 1px 4px rgba(229,62,62,0.5)",
            animation: "liveBlink 1.2s ease-in-out infinite",
            lineHeight: 1, pointerEvents: "none",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {userMenuOpen && (
        <Dropdown minWidth={210}>
          {/* Header: signed-in-as */}
          <div style={{
            padding: "12px 16px 10px",
            borderBottom: "1px solid #f0f0f0",
          }}>
            <div style={{
              fontSize: 11, color: colors.gray400, fontFamily: fonts.body,
              fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6,
            }}>
              {t("nav.signedInAs")}
            </div>
            <div style={{
              fontSize: 14, fontWeight: 700, color: colors.textDark,
              fontFamily: fonts.body, marginTop: 3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {username || userEmail}
            </div>
          </div>

          <DropItem
            to={localize("/feed")}
            icon="📰"
            label={t("nav.feed")}
            active={basePath === "/feed"}
            onClick={closeAll}
          />
          <DropItem
            to={localize("/profile")}
            icon="👤"
            label={t("nav.profile")}
            active={basePath === "/profile"}
            badge={unreadCount > 0 ? (unreadCount > 99 ? "99+" : String(unreadCount)) : null}
            onClick={closeAll}
          />
          {isModerator && (
            <DropItem
              to={localize("/moderator")}
              icon="🛡️"
              label={t("nav.modPanel")}
              active={basePath === "/moderator"}
              onClick={closeAll}
            />
          )}

          <div style={{ height: 1, background: colors.gray200, margin: "4px 0" }} />

          <DropItem
            icon="🚪"
            label={t("nav.logout")}
            onClick={handleLogout}
            danger
          />
        </Dropdown>
      )}
    </div>
  );

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop: dimmt die Seite hinter dem geöffneten mobilen Menü ab und
          schließt es per Klick — macht aus dem Menü ein echtes Drawer-Overlay
          statt eines Akkordeons, das den restlichen Seiteninhalt sichtbar lässt. */}
      {isMobile && menuOpen && (
        <div
          aria-hidden="true"
          onClick={closeAll}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,18,10,0.45)",
            zIndex: 90,
            animation: "fadeIn 0.2s ease-out",
          }}
        />
      )}
      <header
      style={{
        width: "100%",
        background: gradients.header,
        borderBottom: "1.5px solid rgba(222,213,155,0.9)",
        padding: isMobile ? "7px 8px" : "10px 0",
        boxShadow: scrolled
          ? "0 4px 20px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.07)"
          : "0 3px 14px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.25s ease",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxSizing: "border-box",
        backdropFilter: scrolled ? "blur(10px)" : "blur(5px)",
      }}
    >
      <div
        style={{
          maxWidth: layout.maxWidth,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 8px" : "0 18px",
          boxSizing: "border-box",
          gap: 8,
        }}
      >
        {/* Logo / Back button */}
        {isMobile && isDetailPage ? (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              color: colors.green, fontFamily: fonts.body,
              fontWeight: 700, fontSize: 17, padding: "4px 8px",
              borderRadius: radii.sm, userSelect: "none",
            }}
          >
            ← {t("nav.home")}
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flexShrink: 0 }}>
            <Link
              to={localize("/")}
              onClick={handleHomeClick}
              aria-label="Go to homepage"
              style={{
                fontSize: isMobile ? 22 : 26, fontWeight: 900,
                color: colors.greenLogo, letterSpacing: 1.1,
                display: "flex", alignItems: "center", gap: 8,
                fontFamily: fonts.heading, textDecoration: "none",
                textShadow: "0 3px 10px #fffde3, 0 1px 1px #f6ffc2",
                userSelect: "none", whiteSpace: "nowrap",
              }}
            >
              💨 Art-of-Fart.com
            </Link>
            {!isMobile && (
              <span style={{ fontSize: 12.5, color: colors.amberHeader, fontFamily: fonts.body }}>
                {t("hero.subline")}
              </span>
            )}
          </div>
        )}

        {/* ── Desktop navigation ── */}
        {!isMobile && (
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontFamily: fonts.body,
              userSelect: "none",
              flexWrap: "nowrap",
              flexShrink: 0,
              marginLeft: "auto",
            }}
          >
            {renderPrimaryLinks()}
            {renderGalerieMenu()}
            {renderFunMenu()}
            {renderKnowledgeMenu()}

            {/* Separator */}
            <div aria-hidden="true" style={{ width: 1, height: 20, background: "rgba(0,0,0,0.1)", margin: "0 8px", flexShrink: 0 }} />

            {renderLangPicker()}

            <button
              type="button"
              onClick={toggleTheme}
              title={isDark ? "Light Mode" : "Dark Mode"}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              style={{
                background: "none",
                border: `1.5px solid ${colors.borderGreen}`,
                borderRadius: radii.pill,
                width: 34, height: 34,
                cursor: "pointer",
                fontSize: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                transition: "border-color 0.15s, background 0.15s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = colors.successBg; }}
              onMouseOut={(e)  => { e.currentTarget.style.background = "none"; }}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            {isLoggedIn ? renderUserMenu() : (
              <Link
                to={localize("/login")}
                onClick={closeAll}
                style={{
                  marginLeft: 4,
                  background: gradients.loginBtn,
                  color: "#0a3317", // dunkel auf Hellgrün: WCAG AA (weiß war 2,8:1)
                  border: "none",
                  borderRadius: radii.pill,
                  fontWeight: 700,
                  padding: "7px 18px",
                  fontSize: 15,
                  textDecoration: "none",
                  boxShadow: "0 2px 9px rgba(0,0,0,0.18)",
                  transition: "transform 0.12s, box-shadow 0.12s",
                  fontFamily: fonts.body,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 3px 12px rgba(0,0,0,0.2)"; }}
                onMouseOut={(e)  => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "0 2px 9px rgba(0,0,0,0.18)"; }}
              >
                {t("nav.login")}
              </Link>
            )}
          </nav>
        )}

        {/* ── Mobile hamburger ── */}
        {isMobile && (
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen(p => !p)}
              style={{
                background: colors.galleryBg,
                border: `1px solid ${colors.galleryBorder}`,
                borderRadius: radii.sm,
                padding: "7px 9px", fontSize: 22, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)", fontFamily: fonts.body,
                userSelect: "none", display: "flex", alignItems: "center",
                justifyContent: "center", width: 42, height: 42, lineHeight: 1,
              }}
            >
              <span style={{ fontSize: 24 }}>{menuOpen ? "✖" : "☰"}</span>
            </button>
            {unreadCount > 0 && isLoggedIn && (
              <span style={{
                position: "absolute", top: -4, right: -4,
                background: colors.error, color: colors.white, borderRadius: "50%",
                minWidth: 16, height: 16, fontSize: 9, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 3px", boxShadow: "0 1px 4px rgba(229,62,62,0.5)",
                animation: "liveBlink 1.2s ease-in-out infinite",
                lineHeight: 1, pointerEvents: "none",
              }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Mobile dropdown menu ── */}
      {isMobile && menuOpen && (
        <nav
          id="mobile-nav"
          aria-label="Main navigation"
          style={{
            background: colors.creamLight,
            borderRadius: radii.md,
            margin: "6px 10px 4px",
            padding: "12px 14px 10px",
            boxShadow: "0 4px 18px rgba(0,0,0,0.10)",
            fontFamily: fonts.body, userSelect: "none",
            animation: "fadeIn 0.18s ease-out", zIndex: 99,
            position: "relative",
            maxHeight: "calc(100vh - 90px)",
            overflowY: "auto",
          }}
        >
          {renderMobileUserCard()}

          {/* Primäre Aktionen — Hochladen · Battle */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 6px", marginBottom: 2 }}>
            {mobilePrimaryLinks.map(renderMobilePrimaryLink)}
          </div>

          {/* Galerien group — Kernfeature, daher zuoberst */}
          {renderMobileSectionLabel(t("nav.galleries"))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 6px" }}>
            {mobileGalerieLinks.map(renderMobileNavLink)}
          </div>

          {/* Fun group */}
          {renderMobileSectionLabel(`🎮 ${t("nav.fun")}`)}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 6px" }}>
            {mobileFunLinks.map(renderMobileNavLink)}
          </div>

          {/* Wissen group */}
          {renderMobileSectionLabel(`📚 ${t("nav.knowledge")}`)}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 6px", marginBottom: 10 }}>
            {mobileWissenLinks.map(renderMobileNavLink)}
            {isLoggedIn && isModerator && (
              <Link
                to={localize("/moderator")}
                onClick={closeAll}
                style={{
                  color: colors.modText, textDecoration: "none", fontWeight: 700,
                  fontSize: 14, padding: "10px 12px", minHeight: 44, boxSizing: "border-box",
                  borderRadius: 10,
                  background: colors.creamOrange,
                  display: "flex", alignItems: "center", gap: 6,
                  border: "1px solid transparent",
                }}
              >
                🛡️ {t("nav.modPanel")}
              </Link>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: colors.borderGold, margin: "8px 0" }} />

          {/* Bottom row: language + auth */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {renderLangPicker(true)}
              <button
                type="button"
                onClick={toggleTheme}
                title={isDark ? "Light Mode" : "Dark Mode"}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                style={{
                  background: "none",
                  border: `1.5px solid ${colors.borderGreen}`,
                  borderRadius: radii.pill,
                  width: 34, height: 34,
                  cursor: "pointer",
                  fontSize: 16,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {isLoggedIn ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    background: colors.creamLight,
                    border: `1.5px solid ${colors.btnGreen}`,
                    color: colors.greenHeading,
                    borderRadius: radii.pill,
                    fontWeight: 600, padding: "6px 14px",
                    fontSize: 13, cursor: "pointer",
                    fontFamily: fonts.body, whiteSpace: "nowrap",
                  }}
                >
                  {t("nav.logout")}
                </button>
              ) : (
                <Link
                  to={localize("/login")}
                  onClick={closeAll}
                  style={{
                    background: gradients.loginBtn,
                    color: "#0a3317", border: "none", // WCAG AA
                    borderRadius: radii.pill, fontWeight: 700,
                    padding: "7px 18px", fontSize: 14,
                    textDecoration: "none", fontFamily: fonts.body,
                    whiteSpace: "nowrap", display: "inline-block",
                    boxShadow: "0 2px 9px rgba(0,0,0,0.18)",
                  }}
                >
                  {t("nav.login")}
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
    </>
  );
}
